import React, { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    useExpanded,
    useFilters,
    usePagination,
    useRowSelect,
    useSortBy,
    useTable,
} from 'react-table';
import { withTranslation } from 'react-i18next';
import { DaInput, SearchIcon, InternalGrid } from '@paygreen/paygreen-ui';
import { useQueryState } from 'react-router-use-location-state';
import {
    timestampToDateWithHour,
    IsBigDesktop,
    fixExternalUrl,
} from '../../../utils/tools';
import { statusSpaces, statusActivation } from '../../../utils/constants';
import { PaginationTable, StatusFilterTable } from '../../../components';
import { accountTeamManagerMap } from '../shared';
import useTeamMembers from '../hooks/useTeamMembers';
import AdminUsersDaTable from './AdminUsersDaTable';

const AdminUsersTable = ({
    t,
    data,
    fetchData,
    isLoading,
    pageCount: controlledPageCount,
}) => {
    const { teamSales, teamAMs, teamMembers, teamCSMs } = useTeamMembers();
    const [activationFilter, setActivationFilter] = useQueryState(
        'activation',
        '',
    );
    const [accountFilter, setAccountFilter] = useQueryState('account', '');
    const [commercialFilter, setCommercialFilter] = useQueryState('sales', '');
    const [customerSuccessFilter, setCustomerSuccessFilter] = useQueryState(
        'csm',
        '',
    );
    const [spacesFilter, setSpacesFilter] = useState('');
    const [activePageReset, setActivePageReset] = useState(false);
    const [useArrondi, setUseArrondi] = useQueryState('use-arrondi', '');
    const [useTree, setUseTree] = useQueryState('use-tree', '');

    /**
     * @description to apply correct values for each space query parameters based on values names
     * @param {string} value - one of CHARITY | CLIMATE | CLIMATECHARITY
     */
    const handleActivatedSpaces = useCallback(
        (value) => {
            setSpacesFilter(value);

            switch (value) {
                case 'CHARITY':
                    setUseArrondi('1');
                    setUseTree('');
                    break;
                case 'CLIMATE':
                    setUseArrondi('');
                    setUseTree('1');
                    break;
                case 'CLIMATECHARITY':
                    setUseArrondi('1');
                    setUseTree('1');
                    break;
                default:
                    setUseArrondi('');
                    setUseTree('');
            }
        },
        [setUseArrondi, setUseTree],
    );

    const columns = useMemo(
        () => [
            {
                Header: t('admin.users.table.organisationName'),
                id: 'organisation',
                accessor: (d) => [
                    d.organisationName,
                    fixExternalUrl(d.organisationWebsite),
                    d.client_id,
                ],
            },
            {
                Header: t('admin.users.table.user'),
                id: 'user',
                accessor: (d) => {
                    const { firstname, lastname, phone, email } =
                        d.administrator;
                    return [firstname, lastname, phone, email];
                },
            },
            {
                Header: t('admin.users.table.creationDate'),
                id: 'createdAt',
                accessor: (d) =>
                    timestampToDateWithHour(d.administrator.createdAt),
            },
            {
                Header: t('admin.users.table.activationDate'),
                accessor: 'accountEnabled',
                Filter: (props) => (
                    <StatusFilterTable
                        onChange={setActivationFilter}
                        setActivePageReset={setActivePageReset}
                        statusConstants={statusActivation}
                        i18TextBase="admin.users.table.activationStatusFilter"
                        defaultValue={activationFilter}
                        {...props}
                    />
                ),
            },
            {
                Header: t('admin.users.table.activatedSpaces'),
                id: 'spaces',
                accessor: (d) => {
                    const { usesTree, usesArrondi } = d;
                    return [
                        usesTree,
                        usesArrondi,
                        d.climateBillingStatus?.status,
                        d.charityBillingStatus?.status,
                    ];
                },
                Filter: (props) => (
                    <StatusFilterTable
                        onChange={handleActivatedSpaces}
                        setActivePageReset={setActivePageReset}
                        statusConstants={statusSpaces}
                        i18TextBase="admin.users.table.spacesStatusFilter"
                        defaultValue={spacesFilter}
                        {...props}
                    />
                ),
            },
            {
                Header: t('admin.users.table.accountManager'),
                accessor: 'manager',
                Filter: (props) => (
                    <StatusFilterTable
                        onChange={setAccountFilter}
                        setActivePageReset={setActivePageReset}
                        manualOptions={[
                            { value: '', text: t('admin.users.table.all') },
                        ].concat(teamAMs.map(accountTeamManagerMap))}
                        i18TextBase="matching.table.accountManager"
                        defaultValue={accountFilter}
                        {...props}
                    />
                ),
            },
            {
                Header: t('admin.users.table.commercial'),
                accessor: 'commercial',
                Filter: (props) => (
                    <StatusFilterTable
                        onChange={setCommercialFilter}
                        setActivePageReset={setActivePageReset}
                        manualOptions={[
                            { value: '', text: t('admin.users.table.all') },
                        ].concat(teamSales.map(accountTeamManagerMap))}
                        i18TextBase="matching.table.commercial"
                        defaultValue={commercialFilter}
                        {...props}
                    />
                ),
            },
            {
                Header: t('admin.users.table.csm'),
                accessor: 'customerSuccess',
                Filter: (props) => (
                    <StatusFilterTable
                        onChange={setCustomerSuccessFilter}
                        setActivePageReset={setActivePageReset}
                        manualOptions={[
                            { value: '', text: t('admin.users.table.all') },
                        ].concat(teamCSMs.map(accountTeamManagerMap))}
                        i18TextBase="matching.table.csm"
                        defaultValue={customerSuccessFilter}
                        {...props}
                    />
                ),
            },
            {
                Header: 'Actions',
            },
        ],
        [
            t,
            setActivePageReset,
            teamAMs,
            teamSales,
            teamCSMs,
            commercialFilter,
            setAccountFilter,
            setActivationFilter,
            setCustomerSuccessFilter,
            setCommercialFilter,
            accountFilter,
            activationFilter,
            spacesFilter,
            customerSuccessFilter,
            handleActivatedSpaces,
        ],
    );

    const defaultColumn = useMemo(
        () => ({
            // Let's set up our default Filter UI
            Filter: ({ column: { filterValue, setFilter } }) => {
                return (
                    <DaInput
                        value={filterValue || ''}
                        onChange={(e) => {
                            setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
                        }}
                        placeholder={t('admin.users.table.searchInput')}
                        fieldSize="sm"
                        icon={<SearchIcon />}
                    />
                );
            },
        }),
        [t],
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        pageCount,
        setPageSize,
        gotoPage,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            defaultColumn,
            initialState: {
                pageIndex: 0,
                pageSize: 25,
            },
            manualPagination: true,
            pageCount: controlledPageCount,
            manualSortBy: true,
            autoResetPage: false,
            // we ponctually override table internal state to resetPage index with component SelectColumnFilter when needed
            useControlledState: (state) => {
                if (activePageReset) {
                    return { ...state, pageIndex: 0 };
                } else {
                    return state;
                }
            },
        },
        useFilters,
        useSortBy,
        useExpanded,
        usePagination,
        useRowSelect,
    );

    useEffect(() => {
        fetchData({
            pageIndex,
            pageSize,
            activationFilter,
            useArrondi,
            useTree,
            accountFilter,
            commercialFilter,
            customerSuccessFilter,
        });
    }, [
        fetchData,
        pageIndex,
        pageSize,
        activationFilter,
        useArrondi,
        useTree,
        accountFilter,
        commercialFilter,
        customerSuccessFilter,
    ]);

    useEffect(() => {
        // we update spaces filter dynamically each time query params change is detected
        const getActivatedSpacesLabel = () => {
            switch (true) {
                case useTree === '1' && useArrondi === '1':
                    setSpacesFilter('CLIMATECHARITY');
                    break;
                case useArrondi === '1':
                    setSpacesFilter('CHARITY');
                    break;
                case useTree === '1':
                    setSpacesFilter('CLIMATE');
                    break;
                default:
                    setSpacesFilter('');
            }
        };

        getActivatedSpacesLabel();
    }, [useTree, useArrondi]);

    return (
        <InternalGrid
            displayType="grid"
            gridGap="md"
            gridTemplateColumns="1fr"
            blockPadding="none"
            justifyItems="stretch"
            hasStaticWidth={IsBigDesktop() ? false : true}
        >
            <AdminUsersDaTable
                getTableProps={getTableProps}
                getTableBodyProps={getTableBodyProps}
                headerGroups={headerGroups}
                prepareRow={prepareRow}
                page={page}
                isLoading={isLoading}
                data={data}
                teamMembers={teamMembers}
            />

            <PaginationTable
                setPageSize={setPageSize}
                pageCount={pageCount || 1}
                pageIndex={pageIndex}
                colorTheme="quaternary"
                gotoPage={gotoPage}
                setActivePageReset={setActivePageReset}
            />
        </InternalGrid>
    );
};

AdminUsersTable.propTypes = {
    data: PropTypes.array.isRequired,
    pageCount: PropTypes.number,
    isLoading: PropTypes.bool,
    fetchData: PropTypes.func.isRequired,
};

AdminUsersTable.defaultProps = {
    isLoading: false,
    pageCount: 0,
};

export default withTranslation('common')(AdminUsersTable);
